import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  getSketchDesignBlockNames,
  addSketchDesignBlockName,
  addSketchDesignBlock,
} from "../../../../api/projectApi";
import { selectStyles } from "../../../../utils/selectStyles";
import "./AddBlockModal.css";

const CREATE_NEW_OPTION = { value: "__create_new__", label: "+ Создать новый блок" };

const NO_SPOT_OPTION = { value: null, label: "— Без пятна" };

const getBlockSpotId = (b) => b.spot_id ?? b.spot?.id ?? null;

const AddBlockModal = ({ open, onClose, projectId, spots = [], blocks = [], onBlockAdded }) => {
  const [allBlockNames, setAllBlockNames] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(NO_SPOT_OPTION);
  const [newBlockName, setNewBlockName] = useState("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [nameError, setNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const currentSpotId = selectedSpot?.value ?? null;
  const blockNamesInCurrentSpot = React.useMemo(() => {
    return new Set(
      blocks
        .filter((b) => getBlockSpotId(b) === currentSpotId)
        .map((b) => (b.name || "").toLowerCase().trim())
        .filter(Boolean)
    );
  }, [blocks, currentSpotId]);

  const blockNamesOptions = React.useMemo(() => {
    return allBlockNames.filter(
      (opt) => !blockNamesInCurrentSpot.has(opt.label.toLowerCase().trim())
    );
  }, [allBlockNames, blockNamesInCurrentSpot]);

  useEffect(() => {
    if (open) {
      loadBlockNames();
      setSelectedBlocks([]);
      setSelectedSpot(NO_SPOT_OPTION);
      setNewBlockName("");
      setIsCreatingNew(false);
      setNameError("");
    }
  }, [open]);

  const loadBlockNames = async () => {
    try {
      setLoading(true);
      const names = await getSketchDesignBlockNames();
      const options = names.map((name) => ({
        value: name.id,
        label: name.name,
      }));
      setAllBlockNames(options);
    } catch (err) {
      console.error("Ошибка при загрузке названий блоков:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockSelectChange = (selectedOptions) => {
    const selected = selectedOptions || [];
    const hasCreateNew = selected.some((opt) => opt.value === CREATE_NEW_OPTION.value);
    
    if (hasCreateNew) {
      setSelectedBlocks([CREATE_NEW_OPTION]);
      setIsCreatingNew(true);
      setNewBlockName("");
      setNameError("");
    } else {
      setSelectedBlocks(selected);
      setIsCreatingNew(false);
      setNewBlockName("");
      setNameError("");
    }
  };

  const handleNewBlockNameChange = (e) => {
    const value = e.target.value;
    setNewBlockName(value);
    setNameError("");

    if (value.trim()) {
      const valueLower = value.toLowerCase().trim();
      const existsInList = allBlockNames.some(
        (option) => option.label.toLowerCase().trim() === valueLower
      );
      if (existsInList) {
        setNameError("Такое название уже есть в справочнике. Выберите его из списка блоков.");
      }
    }
  };

  const validateForm = () => {
    if (isCreatingNew) {
      if (!newBlockName.trim()) {
        return "Введите название блока";
      }
      if (nameError) {
        return nameError;
      }
    } else {
      if (selectedBlocks.length === 0) {
        return "Выберите блоки из списка";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      return;
    }

    setIsSubmitting(true);
    try {
      const blocksToAdd = [];

      if (isCreatingNew) {
        const newBlockNameData = await addSketchDesignBlockName(newBlockName.trim());
        blocksToAdd.push(newBlockNameData.name);
      } else {
        blocksToAdd.push(...selectedBlocks.map((opt) => opt.label));
      }

      const spotId = selectedSpot?.value ?? null;
      const addedBlocks = [];
      for (const blockNameToUse of blocksToAdd) {
        const payload = {
          project_id: projectId,
          name: blockNameToUse,
          is_active: true,
          area: 0,
          rate: 0,
          floor_coeff: 0,
        };
        if (spotId != null) payload.spot_id = spotId;
        const newBlock = await addSketchDesignBlock(payload);
        addedBlocks.push(newBlock);
      }

      if (onBlockAdded) {
        onBlockAdded(addedBlocks);
      }

      onClose();
    } catch (err) {
      console.error("Ошибка при добавлении блока:", err);
      if (
        err.message?.includes("уже существует") ||
        err.message?.includes("already exists")
      ) {
        setNameError("Такое название уже существует! Выберите его из списка.");
      } else {
        setNameError("Ошибка при добавлении блока. Попробуйте еще раз.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const hasSpots = spots && spots.length > 0;

  const isFormValid = isCreatingNew
    ? newBlockName.trim() && !nameError
    : selectedBlocks.length > 0 && !selectedBlocks.some((opt) => opt.value === CREATE_NEW_OPTION.value);

  return (
    <>
      <div className="sketch-add-block-modal-backdrop" onClick={onClose} />
      <div className="sketch-add-block-modal">
        <div className="sketch-add-block-modal-title">
          Добавление нового блока
        </div>
        <form onSubmit={handleSubmit}>
          <div className="sketch-add-block-modal-content">
            {/* Показываем выбор пятна только если у проекта уже есть пятна; иначе добавляем блоки как раньше — без привязки к пятну */}
            {hasSpots && (
              <div className="sketch-add-block-form-group">
                <label className="sketch-add-block-label">
                  Пятно (опционально)
                </label>
                <Select
                  styles={selectStyles}
                  options={[NO_SPOT_OPTION, ...spots.map((s) => ({ value: s.id, label: s.name }))]}
                  value={selectedSpot}
                  onChange={(opt) => {
                    setSelectedSpot(opt || NO_SPOT_OPTION);
                    setSelectedBlocks([]);
                  }}
                  placeholder="Без пятна"
                  isClearable
                />
              </div>
            )}
            <div className="sketch-add-block-form-group">
              <label className="sketch-add-block-label">
                Выберите блоки из списка*
              </label>
              <Select
                isMulti
                styles={selectStyles}
                options={[CREATE_NEW_OPTION, ...blockNamesOptions]}
                value={selectedBlocks}
                onChange={handleBlockSelectChange}
                placeholder="Начните вводить или выберите из списка..."
                isLoading={loading}
                closeMenuOnSelect={false}
                noOptionsMessage={() =>
                  blockNamesInCurrentSpot.size > 0 && blockNamesOptions.length === 0
                    ? "В этом пятне уже добавлены все блоки из списка"
                    : "Нет доступных блоков"
                }
              />
            </div>

            {isCreatingNew && (
              <div className="sketch-add-block-form-group">
                <label className="sketch-add-block-label">
                  Введите название блока, которое нет в списке*
                </label>
                <div className="sketch-add-block-input-wrapper">
                  <input
                    type="text"
                    value={newBlockName}
                    onChange={handleNewBlockNameChange}
                    placeholder="Введите название..."
                    className={`sketch-add-block-input ${
                      nameError ? "error" : ""
                    }`}
                    autoFocus
                  />
                  {newBlockName.trim() && !nameError && (
                    <span className="sketch-add-block-check">✓</span>
                  )}
                </div>
                {nameError && (
                  <div className="sketch-add-block-error">{nameError}</div>
                )}
              </div>
            )}
          </div>

          <div className="sketch-add-block-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="sketch-add-block-btn-cancel"
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="sketch-add-block-btn-submit"
              disabled={!isFormValid || isSubmitting || !!nameError}
            >
              {isSubmitting ? "Добавление..." : "Добавить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddBlockModal;
